"use client";

import { ListingInquiryDialog } from "@/components/listings/listing-inquiry-dialog";
import { useInquirySender } from "@/hooks/use-inquiry-sender";
import { api } from "@/lib/api-client";
import type { DetailedListing } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

export type ListingMobileInquiryButtonProps = {
  listing: DetailedListing;
  /** Formatted asking price (server-side) so the bar can show it. */
  price: string;
};

export function ListingMobileInquiryButton({
  listing,
  price,
}: ListingMobileInquiryButtonProps) {
  const { user, loaded, senderError } = useInquirySender();
  const [isVisible, setIsVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const apiUtils = api.useUtils();

  const isOwnerOrAgent =
    !!user &&
    (user.id === listing.ownerId || user.id === listing.sellerAgentId);
  const isSold = listing.status === "SOLD";

  useEffect(() => {
    function handleScroll() {
      setIsVisible(window.scrollY > 320);
    }
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (loaded && isOwnerOrAgent) return null;

  return (
    <>
      <div
        className={cn(
          "fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur-md transition-transform duration-300 ease-out md:hidden",
          "pb-[max(env(safe-area-inset-bottom,0px),0.75rem)]",
          isVisible ? "translate-y-0" : "translate-y-full",
        )}
        aria-hidden={!isVisible}
      >
        <div className="mx-auto flex w-full max-w-screen-sm items-center justify-between gap-4 px-4 pt-3">
          <div className="min-w-0">
            <p
              className={cn(
                "font-data text-lg font-semibold leading-tight tracking-tight text-foreground tabular-nums",
                isSold && "line-through text-muted-foreground",
              )}
            >
              {price}
            </p>
            <p className="text-xs text-muted-foreground">
              {isSold ? "Sold" : "Asking price"}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            disabled={!loaded || Boolean(senderError)}
            tabIndex={isVisible ? 0 : -1}
            className="h-12 shrink-0 rounded-xl bg-primary px-6 text-[15px] font-semibold text-primary-foreground transition-transform active:scale-[0.98]"
          >
            Send inquiry
          </button>
        </div>
      </div>

      <ListingInquiryDialog
        listing={listing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSent={() => {
          apiUtils.listings.getListingInquiryConversation.invalidate({
            id: listing.id,
          });
        }}
      />
    </>
  );
}

ListingMobileInquiryButton.displayName = "ListingMobileInquiryButton";
